import React from 'react';
import styled, { css } from 'styled-components';
import { Button, GhostButton, Link } from '../buttons';
import { BreadcumbContainer } from '../buttons/breadcumb';
import { Icon } from '../icon';
import { Steps } from '../steps';

export const useIsPageScrolled = () => {
    const [isScrolled, setIsScrolled] = React.useState(false);

    React.useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }

        const updateScrollState = () => {
            setIsScrolled(window.scrollY > 50);
        };

        updateScrollState();
        window.addEventListener('scroll', updateScrollState, { passive: true });

        return () => {
            window.removeEventListener('scroll', updateScrollState);
        };
    }, []);

    return isScrolled;
};

const scrollGradient = css`
    &::after {
        content: '';
        position: absolute;
        right: 0;
        bottom: -16px;
        left: 0;
        height: 16px;
        pointer-events: none;
        background: linear-gradient(to bottom, ${(props) => props.theme.app.pageBackgroundColor}, transparent);
        opacity: var(--page-header-gradient-opacity, 0);
        transition: opacity 0.2s ease;
    }
`;

const PageHeaderWrap = styled.div<{ $isScrolled?: boolean }>`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    flex-wrap: wrap;
    position: sticky;
    top: 0px;
    z-index: 2;
    gap: 8px;
    background-color: ${(props) => props.theme.app.pageBackgroundColor};
    padding-top: 16px;
    padding-bottom: 16px;
    --page-header-gradient-opacity: ${(props) => (props.$isScrolled ? 1 : 0)};

    ${scrollGradient}
`;

const PageHeaderBackSteps = styled.div`
    display: flex;
    flex: 1 0 100%;
    padding: 0;

    ${Button} {
        margin-left: 10px;
    }
    ${BreadcumbContainer} {
        padding: 0;
        text-transform: none;
        ${Icon} {
            margin-right: 2px;
            margin-left: -7px;
        }
    }
`;
const PageHeaderSteps = styled.div`
    margin-left: auto;
    margin-right: auto;
    line-height: 1.5;
    padding-top: 3px;
    min-height: 26px;
`;
const PageHeaderTitleDescription = styled.div`
    flex: 1 0;
    color: ${(props) => props.theme.app.textColor};
`;
const PageHeaderTitle = styled.h2`
    margin: 0 !important;
    display: flex;
    align-items: center;
    font-size: ${(props) => props.theme.app.component.pageHeader.title.size};
    font-weight: ${(props) => props.theme.app.component.pageHeader.title.weight};
    font-family: ${(props) => props.theme.app.font};
    line-height: ${(props) => props.theme.app.component.pageHeader.lineHeight};
    color: #263238;
`;

const PageHeaderDescription = styled.div<{ $hasDescription: boolean }>`
    padding-block-start: 8px;
    display: flex;
    line-height: ${(props) => props.theme.app.component.pageHeader.lineHeight};
    color: #455a64;
    font-size: 13px;

    > a {
        margin-left: ${(props) => (props.$hasDescription ? '4px' : '0')} !important;
    }

    /* Reset margin for paragraphs inside the description when data is from database */
    > p {
        margin: 0;
    }
`;

const PageHeaderLearnMore = styled(Link)`
    display: inline-block;
`;

const PageHeaderButtonsContainer = styled.div<{ $isScrolled?: boolean }>`
    position: sticky;
    top: 0px;
    z-index: 17;
    background-color: ${(props) => props.theme.app.pageBackgroundColor};
    margin: auto;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    width: 100%;
    --page-header-gradient-opacity: ${(props) => (props.$isScrolled ? 1 : 0)};

    ${scrollGradient}

    ${Button} {
        margin-left: 10px;
    }

    ${BreadcumbContainer} {
        margin-right: auto;
    }
`;

export const PageHeaderButtons = ({ children, className, ...props }: React.HTMLAttributes<HTMLDivElement>) => {
    const isScrolled = useIsPageScrolled();

    return (
        <PageHeaderButtonsContainer className={className} $isScrolled={isScrolled} {...props}>
            {children}
        </PageHeaderButtonsContainer>
    );
};

export const BackAndSteps = styled(PageHeaderButtons)`
    max-width: ${(props) => props.theme.size.container};
    padding: 0 20px 0px 20px;

    ${Steps} {
        width: 100%;
    }
`;

export const FullPageHeaderButtons = styled(PageHeaderButtons)`
    max-width: 100%;
`;

export const PageHeaderButtonsWrap = styled.div`
    display: flex;
    align-items: center;
    align-self: flex-start;
    gap: 8px;
    margin-inline-start: auto;
    flex-shrink: 0;

    ${GhostButton} {
        background-color: ${(props) => props.theme.app.component.button.basic.default.backgroundColor};
        border: ${(props) => props.theme.app.component.button.basic.border};
        color: ${(props) => props.theme.app.component.button.basic.default.textColor};

        &:hover:not([disabled]) {
            background-color: ${(props) => props.theme.app.component.button.basic.hover.backgroundColor};
            color: ${(props) => props.theme.app.component.button.basic.hover.textColor};
        }

        &:active:not([disabled]) {
            background-color: ${(props) => props.theme.app.component.button.basic.active.backgroundColor};
            color: ${(props) => props.theme.app.component.button.basic.active.textColor};
        }
    }
`;

const PageHeaderCompactWrap = styled.div<{ $isScrolled?: boolean }>`
    position: sticky;
    top: 0px;
    z-index: 2;
    padding: 10px 0 24px;
    background-color: ${(props) => props.theme.app.pageBackgroundColor};
    --page-header-gradient-opacity: ${(props) => (props.$isScrolled ? 1 : 0)};

    ${scrollGradient}
`;

const PageHeaderCompactRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;

    ${BreadcumbContainer} {
        padding: 0;
        text-transform: none;
        ${Icon} {
            margin-right: 2px;
            margin-left: -7px;
        }
    }

    ${PageHeaderTitle} {
        flex: 1;
    }
`;

const PageHeaderCompactContainer = ({ children, className }: { children?: React.ReactNode; className?: string }) => {
    const isScrolled = useIsPageScrolled();

    return (
        <PageHeaderCompactWrap className={className} $isScrolled={isScrolled}>
            {children}
        </PageHeaderCompactWrap>
    );
};

PageHeaderContainer.BackSteps = PageHeaderBackSteps;
PageHeaderContainer.Steps = PageHeaderSteps;
PageHeaderContainer.TitleDescription = PageHeaderTitleDescription;
PageHeaderContainer.Title = PageHeaderTitle;
PageHeaderContainer.Description = PageHeaderDescription;
PageHeaderContainer.LearnMore = PageHeaderLearnMore;
PageHeaderContainer.Buttons = PageHeaderButtonsWrap;
PageHeaderContainer.CompactWrap = PageHeaderCompactContainer;
PageHeaderContainer.CompactRow = PageHeaderCompactRow;

export function PageHeaderContainer({ children, className }: { children?: React.ReactNode; className?: string }) {
    const isScrolled = useIsPageScrolled();

    return (
        <PageHeaderWrap className={className} $isScrolled={isScrolled}>
            {children}
        </PageHeaderWrap>
    );
}
